'use client';

import { c as _c } from "react/compiler-runtime";
import type { Data } from '@repo/strapi-types';
import { WistiaPlayer } from '@wistia/wistia-player-react';
import type { ComponentProps } from 'react';
import ReactPlayer from 'react-player';
import { Container, type ContainerProps } from '@/components/elementary/Container';
import { getAspectRatioAttribute } from '@/lib/image-helpers';
import { formatImageUrl } from '@/lib/strapi-helpers';
import type { LayoutContext } from '@/types/general';
type VideoProps = {
  readonly component?: Data.Component<'shared.video'> | null;
  readonly className?: string;
  readonly reactPlayerProps?: ComponentProps<typeof ReactPlayer>;
  readonly layoutContext?: LayoutContext;
  readonly containerPaddingX?: ContainerProps['paddingX'];
  readonly containerPaddingY?: ContainerProps['paddingY'];
  readonly containerPaddingTop?: ContainerProps['paddingTop'];
  readonly containerPaddingBottom?: ContainerProps['paddingBottom'];
};

// Convert YouTube URLs to privacy-enhanced (nocookie) domain
function convertToYouTubeNoCookie(url: string): string {
  if (typeof url !== 'string') return url;

  // Handle all common YouTube URL formats
  return url.replace('https://www.youtube.com/', 'https://www.youtube-nocookie.com/').replace('https://youtube.com/', 'https://www.youtube-nocookie.com/');
}
function getYouTubeThumbnail(url: string): string | false {
  if (typeof url !== 'string') return false;
  const noCookieUrl = convertToYouTubeNoCookie(url);

  // Extract video ID from nocookie URL
  const match = noCookieUrl.match(/youtube-nocookie\.com\/(?:embed\/|watch\?v=)([^?&\s]+)/);
  if (match?.[1]) {
    return `https://i.ytimg.com/vi/${match[1]}/maxresdefault.jpg`;
  }
  return false;
}
export const Video = t0 => {
  const $ = _c(33);
  const {
    component,
    className,
    reactPlayerProps,
    layoutContext,
    containerPaddingX,
    containerPaddingY,
    containerPaddingTop,
    containerPaddingBottom
  } = t0;
  if (!component) {
    return null;
  }
  let t1;
  if ($[0] !== component.media?.url || $[1] !== component.videoLink) {
    t1 = component.videoLink ?? String(formatImageUrl(component.media?.url));
    $[0] = component.media?.url;
    $[1] = component.videoLink;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const rawSrc = t1;
  let aspectRatio;
  let t2;
  if ($[3] !== className || $[4] !== component.anchorId || $[5] !== component.aspectRatio || $[6] !== component.autoplay || $[7] !== component.loop || $[8] !== component.muted || $[9] !== component.playsInline || $[10] !== component.preload || $[11] !== component.showControls || $[12] !== component.wistiaId || $[13] !== component.wistiaPopover || $[14] !== rawSrc || $[15] !== reactPlayerProps) {
    const src = convertToYouTubeNoCookie(rawSrc);
    let t3;
    if ($[18] !== component.aspectRatio) {
      t3 = getAspectRatioAttribute(component.aspectRatio);
      $[18] = component.aspectRatio;
      $[19] = t3;
    } else {
      t3 = $[19];
    }
    aspectRatio = t3.aspectRatio;
    const autoPlay = Boolean(component.autoplay);
    const loop = Boolean(component.loop);
    const muted = autoPlay ? true : Boolean(component.muted);
    const playsInline = Boolean(component.playsInline);
    const preload = component.preload ?? "none";
    const controls = Boolean(component.showControls);
    t2 = component.wistiaId ? <WistiaPlayer id={component.anchorId ?? ""} className={className} style={{
      width: "100%",
      height: "auto",
      aspectRatio
    }} mediaId={component.wistiaId} aspect={1.7777777777777777} autoplay={autoPlay} muted={muted} preload={preload} wistiaPopover={component.wistiaPopover ?? false} popoverAnimateThumbnail={component.wistiaPopover ?? false} /> : <ReactPlayer {...reactPlayerProps} src={src} id={component.anchorId ?? ""} className={className} style={{
      width: "100%",
      height: "auto",
      aspectRatio,
      ...reactPlayerProps?.style
    }} playing={autoPlay} loop={loop} muted={muted} playsInline={playsInline} preload={preload} controls={controls} light={getYouTubeThumbnail(src)} config={{
      youtube: {
        enablejsapi: 0,
        rel: 0
      }
    }} />;
    $[3] = className;
    $[4] = component.anchorId;
    $[5] = component.aspectRatio;
    $[6] = component.autoplay;
    $[7] = component.loop;
    $[8] = component.muted;
    $[9] = component.playsInline;
    $[10] = component.preload;
    $[11] = component.showControls;
    $[12] = component.wistiaId;
    $[13] = component.wistiaPopover;
    $[14] = rawSrc;
    $[15] = reactPlayerProps;
    $[16] = aspectRatio;
    $[17] = t2;
  } else {
    aspectRatio = $[16];
    t2 = $[17];
  }
  const player = t2;
  let t3;
  if ($[20] !== aspectRatio) {
    t3 = {
      aspectRatio
    };
    $[20] = aspectRatio;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== player) {
    t4 = <div className="absolute inset-0">{player}</div>;
    $[22] = player;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== t3 || $[25] !== t4) {
    t5 = <div className="relative w-full" style={t3}>{t4}</div>;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  const videoFrame = t5;
  if (layoutContext === "landing" || layoutContext === "apps-category") {
    let t6;
    if ($[27] !== containerPaddingBottom || $[28] !== containerPaddingTop || $[29] !== containerPaddingX || $[30] !== containerPaddingY || $[31] !== videoFrame) {
      t6 = <Container paddingX={containerPaddingX} paddingY={containerPaddingY} paddingTop={containerPaddingTop} paddingBottom={containerPaddingBottom} className="w-full mx-auto lg:max-w-212.5">{videoFrame}</Container>;
      $[27] = containerPaddingBottom;
      $[28] = containerPaddingTop;
      $[29] = containerPaddingX;
      $[30] = containerPaddingY;
      $[31] = videoFrame;
      $[32] = t6;
    } else {
      t6 = $[32];
    }
    return t6;
  }
  return videoFrame;
};